import { storage, db } from "./firebase";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  arrayUnion,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import EXIF from "exif-js";

const PHOTO_CACHE_KEY = "gr5_photos_cache";
const photoCacheStore = new Map();

function getPhotoCacheKey(baseKey, limitCount) {
  return `${baseKey}_${limitCount ?? "all"}`;
}

function readPhotoCache(cacheKey, ttlMs) {
  const now = Date.now();
  const memoryEntry = photoCacheStore.get(cacheKey);
  if (memoryEntry && now - memoryEntry.timestamp < ttlMs) {
    return memoryEntry.value;
  }

  try {
    const cached = localStorage.getItem(cacheKey);
    const cachedTimestamp = localStorage.getItem(`${cacheKey}_ts`);
    if (cached && cachedTimestamp) {
      const age = now - Number(cachedTimestamp);
      if (age >= 0 && age < ttlMs) {
        const parsed = JSON.parse(cached);
        photoCacheStore.set(cacheKey, { timestamp: now, value: parsed });
        return parsed;
      }
    }
  } catch (error) {
    console.warn("Photo cache read failed:", error);
  }

  return null;
}

function writePhotoCache(cacheKey, value) {
  const now = Date.now();
  photoCacheStore.set(cacheKey, { timestamp: now, value });
  try {
    localStorage.setItem(cacheKey, JSON.stringify(value));
    localStorage.setItem(`${cacheKey}_ts`, String(now));
  } catch (error) {
    console.warn("Photo cache write failed:", error);
  }
}

export function updatePhotoCache(limitCount, photos) {
  const cacheKey = getPhotoCacheKey(PHOTO_CACHE_KEY, limitCount);
  writePhotoCache(cacheKey, photos);
}

export function clearPhotoCache() {
  // Clear all photo cache entries
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(PHOTO_CACHE_KEY)) {
      keysToRemove.push(key);
      if (key.endsWith("_ts")) {
        // Remove timestamp too
        continue;
      }
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));

  // Clear memory cache
  photoCacheStore.clear();
}

// Extract EXIF data from image file
function extractExifData(file) {
  return new Promise((resolve) => {
    EXIF.getData(file, function () {
      try {
        const allMetadata = EXIF.getAllTags(this);

        // Extract GPS data
        const gpsLatitude = EXIF.getTag(this, "GPSLatitude");
        const gpsLongitude = EXIF.getTag(this, "GPSLongitude");
        const gpsLatitudeRef = EXIF.getTag(this, "GPSLatitudeRef");
        const gpsLongitudeRef = EXIF.getTag(this, "GPSLongitudeRef");

        // Check for alternative location tags (sometimes used by iPhone when manually adding location)
        const latitude = EXIF.getTag(this, "Latitude") || gpsLatitude;
        const longitude = EXIF.getTag(this, "Longitude") || gpsLongitude;
        const latitudeRef = EXIF.getTag(this, "LatitudeRef") || gpsLatitudeRef;
        const longitudeRef =
          EXIF.getTag(this, "LongitudeRef") || gpsLongitudeRef;

        // Extract date taken
        const dateTimeOriginal = EXIF.getTag(this, "DateTimeOriginal");
        const createDate = EXIF.getTag(this, "CreateDate");

        // Convert GPS coordinates to decimal format
        let lat = null;
        let lng = null;

        if (latitude && longitude) {
          lat = convertToDecimal(latitude, latitudeRef);
          lng = convertToDecimal(longitude, longitudeRef);
        }

        // If no location found with standard tags, check for decimal format
        if (!lat || !lng) {
          const decimalLat =
            EXIF.getTag(this, "GPSLatitude") || EXIF.getTag(this, "Latitude");
          const decimalLng =
            EXIF.getTag(this, "GPSLongitude") || EXIF.getTag(this, "Longitude");

          if (
            decimalLat &&
            decimalLng &&
            typeof decimalLat === "number" &&
            typeof decimalLng === "number"
          ) {
            lat = decimalLat;
            lng = decimalLng;
          }
        }

        // If still no location, log all metadata for debugging
        if (!lat || !lng) {
          console.log("No location found in EXIF for file:", file.name);
          console.log("All EXIF metadata:", allMetadata);
          // Look for any location-related tags
          const locationTags = Object.keys(allMetadata).filter(
            (key) =>
              key.toLowerCase().includes("gps") ||
              key.toLowerCase().includes("lat") ||
              key.toLowerCase().includes("lon") ||
              key.toLowerCase().includes("location"),
          );
          if (locationTags.length > 0) {
            console.log(
              "Location-related tags found:",
              locationTags.map((tag) => ({ tag, value: allMetadata[tag] })),
            );
          }
        }

        // Convert date to ISO string
        let photoDate = null;
        if (dateTimeOriginal) {
          photoDate = convertExifDateToISO(dateTimeOriginal);
        } else if (createDate) {
          photoDate = convertExifDateToISO(createDate);
        }

        resolve({
          lat,
          lng,
          date: photoDate,
          exifData: allMetadata,
        });
      } catch (error) {
        console.warn("Error extracting EXIF data:", error);
        resolve({ lat: null, lng: null, date: null, exifData: {} });
      }
    });
  });
}

// Convert GPS coordinates from DMS format to decimal
function convertToDecimal(dms, ref) {
  if (!dms || !ref) return null;

  try {
    const degrees = parseFloat(dms[0]) || 0;
    const minutes = parseFloat(dms[1]) || 0;
    const seconds = parseFloat(dms[2]) || 0;

    let decimal = degrees + minutes / 60 + seconds / 3600;

    // Apply direction reference
    if (ref === "S" || ref === "W") {
      decimal = -decimal;
    }

    return isNaN(decimal) ? null : decimal;
  } catch (error) {
    console.warn("Error converting GPS coordinates:", error);
    return null;
  }
}

// Convert EXIF date format to ISO string
function convertExifDateToISO(exifDate) {
  try {
    // EXIF date format is typically "YYYY:MM:DD HH:MM:SS"
    const cleanDate = exifDate.replace(/:/, "-").replace(/:/, "-");
    const date = new Date(cleanDate);

    if (!isNaN(date.getTime())) {
      return date.toISOString();
    }
  } catch (error) {
    console.warn("Error converting EXIF date:", error);
  }

  return null;
}

// Resize image to web-friendly size
async function resizeImageForWeb(file, maxWidth = 600, quality = 0.75) {
  return new Promise(async (resolve, reject) => {
    try {
      const fileType = file.type;

      if (fileType.startsWith("video/")) {
        // Skip resizing for videos
        resolve(file);
        return;
      }

      // Check if file is HEIC format - browsers don't natively support HEIC
      const fileName = file.name.toLowerCase();
      const isHeicFormat =
        fileName.endsWith(".heic") ||
        fileName.endsWith(".heif") ||
        file.type === "image/heic" ||
        file.type === "image/heif";

      if (isHeicFormat) {
        console.log(
          `Processing HEIC file: ${file.name}. Converting to JPEG for resizing.`,
        );
        try {
          // Dynamically import heic2any only when needed
          const heic2anyModule = await import(
            /* webpackChunkName: "heic2any" */ "heic2any"
          );
          const heic2anyFn =
            heic2anyModule && heic2anyModule.default
              ? heic2anyModule.default
              : heic2anyModule;

          // Convert HEIC to JPEG using heic2any
          const jpegBuffer = await heic2anyFn({
            blob: file,
            toType: "image/jpeg",
            quality: 0.75,
          });

          // Create a new File object from the JPEG buffer
          const jpegFile = new File(
            [jpegBuffer],
            file.name.replace(/\.(heic|heif)$/i, ".jpg"),
            {
              type: "image/jpeg",
              lastModified: file.lastModified,
            },
          );

          console.log(`Successfully converted HEIC to JPEG: ${file.name}`);
          // Continue with resizing the converted JPEG file
          file = jpegFile;
        } catch (conversionError) {
          console.error(
            `Failed to convert HEIC file ${file.name}:`,
            conversionError,
          );
          console.warn(
            `Using original HEIC file without resizing: ${file.name}`,
          );
          resolve(file);
          return;
        }
      }

      const img = new Image();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const objectURL = URL.createObjectURL(file);

      img.onload = () => {
        try {
          let { width, height } = img;

          // Calculate new dimensions to maintain aspect ratio with max width
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          // Ensure minimum dimensions
          width = Math.max(1, Math.floor(width));
          height = Math.max(1, Math.floor(height));

          // Set canvas dimensions
          canvas.width = width;
          canvas.height = height;

          // Clear canvas and draw image
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob
          canvas.toBlob(
            (blob) => {
              URL.revokeObjectURL(objectURL); // Clean up
              if (blob && blob.size > 0) {
                // Create a new File object with the resized blob
                const resizedFile = new File([blob], file.name, {
                  type: "image/jpeg",
                  lastModified: file.lastModified,
                });
                resolve(resizedFile);
              } else {
                reject(
                  new Error(
                    "Failed to create resized image blob - empty or invalid",
                  ),
                );
              }
            },
            "image/jpeg",
            quality,
          );
        } catch (error) {
          URL.revokeObjectURL(objectURL); // Clean up
          reject(new Error("Failed to process image: " + error.message));
        }
      };

      img.onerror = (error) => {
        URL.revokeObjectURL(objectURL); // Clean up
        reject(new Error("Failed to load image for resizing"));
      };

      img.src = objectURL;
    } catch (error) {
      reject(new Error("Failed to resize image: " + error.message));
    }
  });
}

// Create thumbnail from file
async function createThumbnailFromFile(file) {
  return new Promise((resolve, reject) => {
    try {
      const fileType = file.type;

      if (fileType.startsWith("video/")) {
        // Skip thumbnail creation for videos - they don't need thumbnails
        resolve(null);
      } else {
        // Handle image thumbnail creation
        // Check if file is HEIC format - browsers don't natively support HEIC
        const fileName = file.name.toLowerCase();
        const isHeicFormat =
          fileName.endsWith(".heic") ||
          fileName.endsWith(".heif") ||
          file.type === "image/heic" ||
          file.type === "image/heif";

        if (isHeicFormat) {
          console.warn(
            `Skipping thumbnail creation for HEIC file: ${file.name}. HEIC format is not supported by browsers for thumbnail generation. Original image will be used instead.`,
          );
          // Don't reject, but resolve with null to indicate no thumbnail was created
          resolve(null);
          return;
        }

        const img = new Image();
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const objectURL = URL.createObjectURL(file);

        img.onload = () => {
          try {
            // Calculate new dimensions to maintain aspect ratio with max 200x200
            let { width, height } = img;
            const maxSize = 200;

            if (width > height) {
              if (width > maxSize) {
                height = (height * maxSize) / width;
                width = maxSize;
              }
            } else {
              if (height > maxSize) {
                width = (width * maxSize) / height;
                height = maxSize;
              }
            }

            // Ensure minimum dimensions
            width = Math.max(1, Math.floor(width));
            height = Math.max(1, Math.floor(height));

            // Set canvas dimensions
            canvas.width = width;
            canvas.height = height;

            // Clear canvas and draw image
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);

            // Convert to blob
            canvas.toBlob(
              (blob) => {
                URL.revokeObjectURL(objectURL); // Clean up
                if (blob && blob.size > 0) {
                  resolve(blob);
                } else {
                  reject(
                    new Error(
                      "Failed to create thumbnail blob - empty or invalid",
                    ),
                  );
                }
              },
              "image/jpeg",
              0.8,
            );
          } catch (error) {
            URL.revokeObjectURL(objectURL); // Clean up
            reject(new Error("Failed to process image: " + error.message));
          }
        };

        img.onerror = (error) => {
          URL.revokeObjectURL(objectURL); // Clean up
          reject(new Error("Failed to load image for thumbnail"));
        };

        img.src = objectURL;
      }
    } catch (error) {
      reject(new Error("Failed to create thumbnail: " + error.message));
    }
  });
}

// Upload photo to Firebase Storage with automatic EXIF data extraction and thumbnail creation
export async function uploadPhoto(file, hikeId, photoData) {
  try {
    // Validate inputs
    if (!file) {
      throw new Error("No file provided");
    }
    if (!hikeId) {
      throw new Error("No hike ID provided");
    }
    if (!photoData) {
      photoData = {};
    }

    // Extract EXIF data from the photo
    const exifData = await extractExifData(file);

    // Resize image to web-friendly size if it's an image
    let fileToUpload = file;
    try {
      if (file.type.startsWith("image/")) {
        console.log("Resizing image for web:", file.name);
        fileToUpload = await resizeImageForWeb(file);
        console.log(`Resized from ${file.size} to ${fileToUpload.size} bytes`);
      }
    } catch (resizeError) {
      console.warn("Failed to resize image, using original:", resizeError);
      fileToUpload = file;
    }

    // Use EXIF data if available, otherwise fall back to provided data
    const finalLat = exifData.lat || photoData.lat;
    const finalLng = exifData.lng || photoData.lng;
    const finalDate =
      exifData.date || photoData.date || new Date().toISOString();

    // Create unique filenames
    const timestamp = new Date().getTime();
    const fileName = `${hikeId}_${timestamp}_${file.name}`;
    const thumbnailFileName = `thumb_${fileName}`;

    // Upload original file (resized if applicable)
    const storageRef = ref(storage, `photos/${hikeId}/${fileName}`);
    const snapshot = await uploadBytes(storageRef, fileToUpload);
    const downloadURL = await getDownloadURL(snapshot.ref);

    // Create and upload thumbnail
    let thumbnailURL = null;
    try {
      console.log("Creating thumbnail for file:", file.name);
      const thumbnailBlob = await createThumbnailFromFile(file);

      // Skip upload if thumbnail creation returned null (e.g., for HEIC files)
      if (thumbnailBlob === null) {
        console.log(
          "Thumbnail creation skipped for unsupported format:",
          file.name,
        );
        thumbnailURL = null;
      } else {
        console.log("Thumbnail blob created, size:", thumbnailBlob.size);

        const thumbnailStorageRef = ref(
          storage,
          `photos/${hikeId}/${thumbnailFileName}`,
        );
        const thumbnailSnapshot = await uploadBytes(
          thumbnailStorageRef,
          thumbnailBlob,
        );
        thumbnailURL = await getDownloadURL(thumbnailSnapshot.ref);
        console.log("Thumbnail uploaded successfully:", thumbnailURL);
      }
    } catch (thumbnailError) {
      console.error(
        "Failed to create/upload thumbnail for",
        file.name,
        ":",
        thumbnailError,
      );
      // Continue without thumbnail - it's not critical
    }

    // Sanitize EXIF data to remove incompatible objects for Firestore
    const sanitizeExifData = (exifObj) => {
      if (!exifObj || typeof exifObj !== "object") return {};

      const sanitized = {};
      for (const [key, value] of Object.entries(exifObj)) {
        // Only include primitive types and simple arrays
        if (
          value === null ||
          value === undefined ||
          typeof value === "string" ||
          typeof value === "number" ||
          typeof value === "boolean" ||
          (Array.isArray(value) &&
            value.every(
              (item) =>
                typeof item === "string" ||
                typeof item === "number" ||
                typeof item === "boolean",
            ))
        ) {
          sanitized[key] = value;
        }
        // Skip complex objects, functions, and custom Number objects
      }
      return sanitized;
    };

    // Save photo metadata to Firestore
    const photoMetadata = {
      url: downloadURL,
      thumbnailUrl: thumbnailURL, // Store thumbnail URL
      caption: photoData.caption || "",
      description: photoData.description || "",
      lat: finalLat || null, // Convert undefined to null for Firestore
      lng: finalLng || null, // Convert undefined to null for Firestore
      date: finalDate,
      hikeId: hikeId,
      fileName: fileName,
      thumbnailFileName: thumbnailFileName,
      originalName: file.name,
      size: fileToUpload.size,
      originalSize: file.size,
      type: file.type,
      uploadedAt: new Date().toISOString(),
      hasExifData: !!(exifData.lat && exifData.lng),
      exifData: sanitizeExifData(exifData.exifData),
      resized: fileToUpload !== file,
      ...photoData,
    };

    const photoDoc = await addDoc(collection(db, "photos"), photoMetadata);

    // Add photo to the hike's photos array (always, regardless of coordinates)
    await addPhotoToHike(hikeId, {
      id: photoDoc.id,
      url: downloadURL,
      thumbnailUrl: thumbnailURL || null,
      caption: photoData.caption || "",
      description: photoData.description || "",
      lat: finalLat || null,
      lng: finalLng || null,
      date: finalDate || null,
      hikeId: hikeId,
    });

    // Clear cache to ensure fresh data on next fetch
    clearPhotoCache();

    return {
      success: true,
      photoId: photoDoc.id,
      downloadURL: downloadURL,
      thumbnailURL: thumbnailURL,
      hasExifData: !!(exifData.lat && exifData.lng),
      exifExtracted: exifData.lat ? true : false,
    };
  } catch (error) {
    console.error("Error uploading photo:", error);
    return {
      success: false,
      error: error.message,
    };
  }
}

// Upload multiple photos at once with batch EXIF processing
export async function uploadMultiplePhotos(
  files,
  hikeId,
  globalCaption = "",
  onProgress = null,
) {
  try {
    const results = [];
    const errors = [];

    // Process files sequentially to avoid overwhelming the system
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Report progress
      if (onProgress) {
        onProgress({
          current: i + 1,
          total: files.length,
          currentFile: file.name,
          status: "processing",
        });
      }

      try {
        // Create individual photo data
        const photoData = {
          caption: globalCaption || "", // Use global caption if provided
          description: "",
        };

        const result = await uploadPhoto(file, hikeId, photoData);

        if (result.success) {
          results.push({
            fileName: file.name,
            photoId: result.photoId,
            hasExifData: result.hasExifData,
            exifExtracted: result.exifExtracted,
          });

          // Report success
          if (onProgress) {
            onProgress({
              current: i + 1,
              total: files.length,
              currentFile: file.name,
              status: "success",
              result: result,
            });
          }
        } else {
          errors.push({
            fileName: file.name,
            error: result.error,
          });

          // Report error
          if (onProgress) {
            onProgress({
              current: i + 1,
              total: files.length,
              currentFile: file.name,
              status: "error",
              error: result.error,
            });
          }
        }
      } catch (error) {
        errors.push({
          fileName: file.name,
          error: error.message,
        });

        // Report error
        if (onProgress) {
          onProgress({
            current: i + 1,
            total: files.length,
            currentFile: file.name,
            status: "error",
            error: error.message,
          });
        }
      }
    }

    // Final progress report
    if (onProgress) {
      onProgress({
        current: files.length,
        total: files.length,
        status: "completed",
        summary: {
          total: files.length,
          successful: results.length,
          failed: errors.length,
          withExif: results.filter((r) => r.hasExifData).length,
        },
      });
    }

    return {
      success: true,
      uploaded: results,
      failed: errors,
      summary: {
        total: files.length,
        successful: results.length,
        failed: errors.length,
        withExif: results.filter((r) => r.hasExifData).length,
      },
    };
  } catch (error) {
    console.error("Error in batch upload:", error);

    // Report final error
    if (onProgress) {
      onProgress({
        status: "failed",
        error: error.message,
      });
    }

    return {
      success: false,
      error: error.message,
    };
  }
}

// Add photo to hike's photos array
async function addPhotoToHike(hikeId, photo) {
  try {
    const hikeRef = doc(db, "hikes", hikeId);

    try {
      // Use arrayUnion to append without reading the full document
      await updateDoc(hikeRef, {
        photos: arrayUnion(photo),
      });
    } catch (err) {
      // Fallback: if the doc doesn't exist yet, create it with the photo array
      console.warn(
        "updateDoc failed when adding photo to hike; attempting setDoc",
        err,
      );
      await setDoc(hikeRef, { photos: [photo] }, { merge: true });
    }
  } catch (error) {
    console.error("Error adding photo to hike:", error);
  }
}

// Get all photos for a specific hike (excluding deleted photos)
export async function getPhotosForHike(hikeId) {
  try {
    // Get all photos for the hike and filter out deleted ones client-side
    const photosQuery = query(
      collection(db, "photos"),
      where("hikeId", "==", hikeId),
      orderBy("uploadedAt", "desc"),
    );

    const querySnapshot = await getDocs(photosQuery);
    const photos = [];

    querySnapshot.forEach((doc) => {
      const photoData = doc.data();
      // Only include photos that are not marked as deleted (deletedAt is null, undefined, or doesn't exist)
      if (!photoData.deletedAt) {
        photos.push({
          id: doc.id,
          ...photoData,
        });
      }
    });

    return photos;
  } catch (error) {
    console.error("Error fetching photos for hike:", error);
    return [];
  }
}

// Get all photos from Firebase (excluding deleted photos)
export async function getAllPhotos(options = {}) {
  try {
    const {
      limitCount = null,
      useCache = true,
      cacheTtlMs = 30 * 60 * 1000, // 30 minutes
    } = options;
    const cacheKey = getPhotoCacheKey(PHOTO_CACHE_KEY, limitCount);

    if (useCache) {
      const cached = readPhotoCache(cacheKey, cacheTtlMs);
      if (cached) {
        return cached;
      }
    }

    // Get all photos and filter out deleted ones client-side
    let photosQuery = query(
      collection(db, "photos"),
      orderBy("uploadedAt", "desc"),
    );
    if (limitCount) {
      photosQuery = query(
        collection(db, "photos"),
        orderBy("uploadedAt", "desc"),
        limit(limitCount),
      );
    }

    const querySnapshot = await getDocs(photosQuery);
    const photos = [];

    querySnapshot.forEach((doc) => {
      const photoData = doc.data();
      // Only include photos that are not marked as deleted (deletedAt is null, undefined, or doesn't exist)
      if (!photoData.deletedAt) {
        photos.push({
          id: doc.id,
          ...photoData,
        });
      }
    });

    if (useCache) {
      writePhotoCache(cacheKey, photos);
    }

    return photos;
  } catch (error) {
    console.error("Error fetching all photos:", error);
    return [];
  }
}

export async function getPhotosSince(lastUploadedAt) {
  try {
    if (!lastUploadedAt) return [];
    const photosQuery = query(
      collection(db, "photos"),
      orderBy("uploadedAt", "desc"),
      where("uploadedAt", ">", lastUploadedAt),
    );

    const querySnapshot = await getDocs(photosQuery);
    const photos = [];

    querySnapshot.forEach((doc) => {
      const photoData = doc.data();
      if (!photoData.deletedAt) {
        photos.push({
          id: doc.id,
          ...photoData,
        });
      }
    });

    return photos;
  } catch (error) {
    console.error("Error fetching new photos:", error);
    return [];
  }
}

// Delete photo (complete cleanup from Firebase - Firestore + Storage)
export async function deletePhoto(photoId, hikeId) {
  try {
    console.log("Deleting photo:", photoId, "from hike:", hikeId);

    // First, get the photo data to know the file paths
    const photoRef = doc(db, "photos", photoId);
    const photoDoc = await getDoc(photoRef);

    if (!photoDoc.exists()) {
      console.warn("Photo not found in Firestore:", photoId);
      return { success: false, error: "Photo not found" };
    }

    const photoData = photoDoc.data();
    console.log("Photo data:", photoData);

    // Delete original image from Firebase Storage
    try {
      const originalImagePath = `photos/${photoData.hikeId}/${photoData.fileName}`;
      const originalImageRef = ref(storage, originalImagePath);
      await deleteObject(originalImageRef);
      console.log("Original image deleted from storage:", originalImagePath);
    } catch (storageError) {
      if (storageError.code === "storage/object-not-found") {
        console.log(
          "Original image not found in storage (may have been already deleted):",
          `photos/${photoData.hikeId}/${photoData.fileName}`,
        );
      } else {
        console.warn(
          "Failed to delete original image from storage:",
          storageError,
        );
      }
    }

    // Delete thumbnail from Firebase Storage (if it exists)
    if (photoData.thumbnailFileName) {
      try {
        const thumbnailPath = `photos/${photoData.hikeId}/${photoData.thumbnailFileName}`;
        const thumbnailRef = ref(storage, thumbnailPath);
        await deleteObject(thumbnailRef);
        console.log("Thumbnail deleted from storage:", thumbnailPath);
      } catch (thumbnailError) {
        if (thumbnailError.code === "storage/object-not-found") {
          console.log(
            "Thumbnail not found in storage (may have been already deleted):",
            `photos/${photoData.hikeId}/${photoData.thumbnailFileName}`,
          );
        } else {
          console.warn(
            "Failed to delete thumbnail from storage:",
            thumbnailError,
          );
        }
      }
    }

    // Delete the photo document from Firestore
    await deleteDoc(photoRef);
    console.log("Photo document deleted from Firestore:", photoId);

    // Remove from hike's photos array with proper matching
    try {
      // Use the hikeId from the photo data to ensure we remove from the correct hike
      const correctHikeId = photoData.hikeId || hikeId;

      if (!correctHikeId) {
        console.warn("No hikeId found in photo data or parameters");
        throw new Error("Cannot determine which hike to update");
      }

      const hikeRef = doc(db, "hikes", correctHikeId);
      const hikeDoc = await getDoc(hikeRef);

      if (hikeDoc.exists()) {
        const hikeData = hikeDoc.data();
        const currentPhotos = hikeData.photos || [];

        console.log(
          `Current photos array length before filtering: ${currentPhotos.length}`,
          `Looking for photoId: ${photoId}`,
        );

        // Filter out the photo by comparing the id property
        // This ensures we match by ID regardless of other properties
        const updatedPhotos = currentPhotos.filter((photo) => {
          if (!photo) {
            console.warn("Found null/undefined photo in array");
            return false;
          }
          const matches = photo.id === photoId;
          if (matches) {
            console.log(`Found matching photo to remove: ${photo.id}`);
          }
          return !matches; // Keep photos that DON'T match
        });

        // Only update if photos array changed
        if (updatedPhotos.length !== currentPhotos.length) {
          await updateDoc(hikeRef, {
            photos: updatedPhotos,
          });
          console.log(
            `✓ Photo removed from hike photos array. Before: ${currentPhotos.length}, After: ${updatedPhotos.length}`,
          );
        } else {
          console.warn(
            `⚠️ Photo ${photoId} not found in hike ${correctHikeId} photos array. This may indicate the photo was already removed or the hikeId mismatch.`,
          );
        }
      } else {
        console.warn(`Hike ${correctHikeId} not found in Firestore`);
      }
    } catch (hikeUpdateError) {
      console.error(
        "Error removing photo from hike photos array:",
        hikeUpdateError,
      );
      // Don't fail the entire deletion if hike update fails
      // The photo is already deleted from storage and Firestore
    }

    console.log("Photo completely deleted (Firestore + Storage):", photoId);

    // Clear cache to ensure fresh data on next fetch
    clearPhotoCache();

    return { success: true };
  } catch (error) {
    console.error("Error deleting photo:", error);
    return {
      success: false,
      error: error.message,
    };
  }
}

// Update photo metadata
export async function updatePhoto(photoId, updates) {
  try {
    await updateDoc(doc(db, "photos", photoId), {
      ...updates,
      updatedAt: new Date().toISOString(),
    });

    // Clear cache to ensure fresh data on next fetch
    clearPhotoCache();

    return { success: true };
  } catch (error) {
    console.error("Error updating photo:", error);
    return {
      success: false,
      error: error.message,
    };
  }
}

// Create thumbnail from image URL
export async function createThumbnail(imageUrl, maxWidth = 50, maxHeight = 50) {
  try {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Calculate new dimensions to maintain aspect ratio
        let { width, height } = img;

        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw and compress image
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to base64 data URL with compression
        const thumbnailUrl = canvas.toDataURL("image/jpeg", 0.7);
        resolve(thumbnailUrl);
      };

      img.onerror = () => {
        // Fallback to original image URL if thumbnail creation fails
        resolve(imageUrl);
      };

      img.src = imageUrl;
    });
  } catch (error) {
    console.warn("Error creating thumbnail:", error);
    // Fallback to original image URL
    return imageUrl;
  }
}

// Batch update multiple photos
export async function updateMultiplePhotos(updates) {
  try {
    const promises = updates.map(({ photoId, updates: photoUpdates }) =>
      updatePhoto(photoId, photoUpdates),
    );

    const results = await Promise.all(promises);

    return {
      success: true,
      results: results,
      failed: results.filter((r) => !r.success).length,
      successful: results.filter((r) => r.success).length,
    };
  } catch (error) {
    console.error("Error in batch update:", error);
    return {
      success: false,
      error: error.message,
    };
  }
}

// Create thumbnail for existing photo without one
export async function createThumbnailForPhoto(photoId, photoUrl, hikeId) {
  try {
    // Fetch the photo document
    const photoRef = doc(db, "photos", photoId);
    const photoDoc = await getDoc(photoRef);

    if (!photoDoc.exists()) {
      throw new Error("Photo not found");
    }

    const photoData = photoDoc.data();

    // If thumbnail already exists, return it
    if (photoData.thumbnailUrl) {
      return { success: true, thumbnailUrl: photoData.thumbnailUrl };
    }

    // Create thumbnail from existing image
    const thumbnailUrl = await createThumbnail(photoUrl, 200, 200);

    if (thumbnailUrl && thumbnailUrl !== photoUrl) {
      // Upload thumbnail to storage
      const timestamp = new Date().getTime();
      const thumbnailFileName = `thumb_${
        photoData.fileName || `${photoId}_${timestamp}.jpg`
      }`;
      const thumbnailStorageRef = ref(
        storage,
        `photos/${hikeId}/${thumbnailFileName}`,
      );

      // Convert data URL to blob
      const response = await fetch(thumbnailUrl);
      const blob = await response.blob();

      const thumbnailSnapshot = await uploadBytes(thumbnailStorageRef, blob);
      const uploadedThumbnailUrl = await getDownloadURL(thumbnailSnapshot.ref);

      // Update photo document with thumbnail URL
      await updateDoc(photoRef, {
        thumbnailUrl: uploadedThumbnailUrl,
        thumbnailFileName: thumbnailFileName,
        thumbnailCreatedAt: new Date().toISOString(),
      });

      return { success: true, thumbnailUrl: uploadedThumbnailUrl };
    }

    return { success: false, error: "Failed to create thumbnail" };
  } catch (error) {
    console.error("Error creating thumbnail for photo:", error);
    return { success: false, error: error.message };
  }
}

// Batch create thumbnails for photos that don't have them
export async function createThumbnailsForExistingPhotos() {
  try {
    const photos = await getAllPhotos();
    const photosWithoutThumbnails = photos.filter(
      (photo) => !photo.thumbnailUrl,
    );

    const results = [];

    for (const photo of photosWithoutThumbnails) {
      try {
        const result = await createThumbnailForPhoto(
          photo.id,
          photo.url,
          photo.hikeId,
        );
        results.push({
          photoId: photo.id,
          fileName: photo.originalName,
          success: result.success,
          error: result.error,
        });
      } catch (error) {
        results.push({
          photoId: photo.id,
          fileName: photo.originalName,
          success: false,
          error: error.message,
        });
      }
    }

    return {
      success: true,
      total: photosWithoutThumbnails.length,
      results: results,
      successful: results.filter((r) => r.success).length,
      failed: results.filter((r) => !r.success).length,
    };
  } catch (error) {
    console.error("Error in batch thumbnail creation:", error);
    return { success: false, error: error.message };
  }
}

// Get photos with thumbnails (optimized for admin panel)
export async function getPhotosWithThumbnails() {
  try {
    const photos = await getAllPhotos();

    // Use stored thumbnail URLs or create them client-side as fallback
    const photosWithThumbnails = await Promise.all(
      photos.map(async (photo) => ({
        ...photo,
        thumbnail: photo.thumbnailUrl || (await createThumbnail(photo.url)),
      })),
    );

    return photosWithThumbnails;
  } catch (error) {
    console.error("Error fetching photos with thumbnails:", error);
    return [];
  }
}

// Debug function to show what files would be deleted for a photo
export async function debugPhotoDeletion(photoId) {
  try {
    console.log(`Debugging photo deletion for ID: ${photoId}`);

    const photoRef = doc(db, "photos", photoId);
    const photoDoc = await getDoc(photoRef);

    if (!photoDoc.exists()) {
      console.log("Photo not found in Firestore");
      return { success: false, error: "Photo not found" };
    }

    const photoData = photoDoc.data();
    console.log("Photo data:", photoData);

    const originalImagePath = photoData.fileName
      ? `photos/${photoData.hikeId}/${photoData.fileName}`
      : null;
    const thumbnailPath = photoData.thumbnailFileName
      ? `photos/${photoData.hikeId}/${photoData.thumbnailFileName}`
      : null;

    const debugInfo = {
      photoId: photoId,
      photoData: photoData,
      filesToDelete: {
        original: originalImagePath,
        thumbnail: thumbnailPath,
      },
      fileNames: {
        original: photoData.fileName,
        thumbnail: photoData.thumbnailFileName,
      },
      hikeId: photoData.hikeId,
    };

    console.log("Debug info for photo deletion:", debugInfo);
    return { success: true, debugInfo };
  } catch (error) {
    console.error("Error debugging photo deletion:", error);
    return { success: false, error: error.message };
  }
}

// Find and clean up orphaned photo references in hikes
export async function cleanupOrphanedPhotoReferences() {
  try {
    console.log("Starting cleanup of orphaned photo references...");

    // Get all hikes
    const hikesQuery = query(collection(db, "hikes"));
    const hikesSnapshot = await getDocs(hikesQuery);

    // Get all valid photo IDs from the photos collection
    const photosQuery = query(collection(db, "photos"));
    const photosSnapshot = await getDocs(photosQuery);
    const validPhotoIds = new Set();

    photosSnapshot.forEach((doc) => {
      validPhotoIds.add(doc.id);
    });

    console.log(`Found ${validPhotoIds.size} valid photos in database`);

    let totalOrphaned = 0;
    let hikesUpdated = 0;

    // Check each hike for orphaned photo references
    for (const hikeDoc of hikesSnapshot.docs) {
      const hikeData = hikeDoc.data();
      const currentPhotos = hikeData.photos || [];

      if (currentPhotos.length === 0) continue;

      // Filter out any photos that don't exist in the photos collection
      const cleanedPhotos = currentPhotos.filter((photo) => {
        if (!photo || !photo.id) {
          console.warn(`Invalid photo object in hike ${hikeDoc.id}:`, photo);
          return false;
        }
        const isValid = validPhotoIds.has(photo.id);
        if (!isValid) {
          console.warn(
            `Found orphaned photo reference: ${photo.id} in hike ${hikeDoc.id}`,
          );
          totalOrphaned++;
        }
        return isValid;
      });

      // Update hike if we removed any orphaned references
      if (cleanedPhotos.length < currentPhotos.length) {
        await updateDoc(doc(db, "hikes", hikeDoc.id), {
          photos: cleanedPhotos,
        });
        console.log(
          `✓ Updated hike ${hikeDoc.id}: removed ${
            currentPhotos.length - cleanedPhotos.length
          } orphaned references`,
        );
        hikesUpdated++;
      }
    }

    console.log(
      `Cleanup complete: ${totalOrphaned} orphaned references removed from ${hikesUpdated} hikes`,
    );
    return {
      success: true,
      totalOrphaned,
      hikesUpdated,
    };
  } catch (error) {
    console.error("Error cleaning up orphaned photo references:", error);
    return {
      success: false,
      error: error.message,
    };
  }
}

// Batch cleanup orphaned storage files
export async function cleanupOrphanedStorageFiles() {
  try {
    console.log("Starting cleanup of orphaned storage files...");

    // Get all photos from Firestore
    const allPhotosQuery = query(
      collection(db, "photos"),
      orderBy("uploadedAt", "desc"),
    );
    const querySnapshot = await getDocs(allPhotosQuery);

    const validFilePaths = new Set();

    // Build a set of all valid file paths
    querySnapshot.forEach((doc) => {
      const photoData = doc.data();
      if (photoData.fileName) {
        validFilePaths.add(`photos/${photoData.hikeId}/${photoData.fileName}`);
      }
      if (photoData.thumbnailFileName) {
        validFilePaths.add(
          `photos/${photoData.hikeId}/${photoData.thumbnailFileName}`,
        );
      }
    });

    console.log(`Found ${validFilePaths.size} valid file paths in Firestore`);

    // Note: Firebase Storage doesn't have a direct way to list all files
    // This is a limitation of the Firebase Storage API
    // In a real production environment, you would need to maintain a separate
    // index of all uploaded files or use a different storage solution
    // that supports file listing

    console.log(
      "Storage cleanup completed. Note: Manual verification recommended.",
    );
    console.log("Valid file paths:", Array.from(validFilePaths));

    return {
      success: true,
      validFilePaths: Array.from(validFilePaths),
      message:
        "Cleanup completed. Manual verification recommended due to Storage API limitations.",
    };
  } catch (error) {
    console.error("Error during storage cleanup:", error);
    return {
      success: false,
      error: error.message,
    };
  }
}
